import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Upload, X, Check, Image as ImageIcon } from 'lucide-react';
import { uploadMedia, bulkUploadMedia, getMediaFiles, deleteMedia } from '../services/api';
import { toast } from 'sonner';

export const MediaLibrary = ({ onSelect, isOpen, onClose }) => {
  const [mediaFiles, setMediaFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [previewImage, setPreviewImage] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (isOpen) {
      loadMedia();
    }
  }, [isOpen]);

  const loadMedia = async () => {
    try {
      setLoading(true);
      const files = await getMediaFiles();
      setMediaFiles(files);
    } catch (error) {
      toast.error('Failed to load media files');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      if (files.length > 1) {
        // Use bulk upload for multiple files
        const result = await bulkUploadMedia(files);
        if (result.failed > 0) {
          toast.warning(`Uploaded ${result.successful} files, ${result.failed} failed`);
        } else {
          toast.success(`${result.successful} file(s) uploaded successfully`);
        }
      } else {
        // Single file upload
        await uploadMedia(files[0]);
        toast.success('File uploaded successfully');
      }
      loadMedia();
    } catch (error) {
      toast.error('Failed to upload files');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      await deleteMedia(id);
      toast.success('File deleted successfully');
      loadMedia();
    } catch (error) {
      toast.error('Failed to delete file');
      console.error(error);
    }
  };

  const toggleSelection = (fileId) => {
    const newSelected = new Set(selectedFiles);
    if (newSelected.has(fileId)) {
      newSelected.delete(fileId);
    } else {
      newSelected.add(fileId);
    }
    setSelectedFiles(newSelected);
  };

  const handleSelectFiles = () => {
    if (onSelect) {
      // Return CDN URLs from R2
      const selectedUrls = Array.from(selectedFiles).map(fileId => {
        const file = mediaFiles.find(f => f.id === fileId);
        return file?.url || '';
      }).filter(url => url);
      onSelect(selectedUrls);
    }
    setSelectedFiles(new Set());
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Media Library</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {/* Upload Section */}
          <div className="mb-6">
            <label className="flex items-center justify-center w-full h-32 px-4 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              <div className="text-center">
                {uploading ? (
                  <p className="text-gray-600 dark:text-gray-400">Uploading...</p>
                ) : (
                  <>
                    <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Click to upload images
                    </p>
                  </>
                )}
              </div>
              <Input
                type="file"
                className="hidden"
                accept="image/*"
                multiple
                onChange={handleFileUpload}
                disabled={uploading}
              />
            </label>
          </div>

          {/* Media Grid */}
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-600 dark:text-gray-400">Loading media...</p>
            </div>
          ) : mediaFiles.length === 0 ? (
            <div className="text-center py-12">
              <ImageIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-gray-400">No media files yet</p>
              <p className="text-sm text-gray-500 dark:text-gray-500">Upload some images to get started</p>
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-4">
              {mediaFiles.map((file) => {
                // Use CDN URL from R2 (already a full URL)
                const imageUrl = file.url;
                const isSelected = selectedFiles.has(file.id);
                
                return (
                  <div
                    key={file.id}
                    className="relative group"
                  >
                    <div
                      onClick={() => toggleSelection(file.id)}
                      className={`relative h-32 rounded-lg overflow-hidden cursor-pointer border-2 transition-all flex items-center justify-center bg-gray-100 dark:bg-gray-800 ${
                        isSelected 
                          ? 'border-blue-700 ring-2 ring-blue-700' 
                          : 'border-transparent hover:border-gray-300 dark:hover:border-gray-700'
                      }`}
                    >
                      <img
                        src={imageUrl}
                        alt={file.original_filename}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          console.error('Image load error:', imageUrl);
                          e.target.style.display = 'none';
                        }}
                      />
                      {isSelected && (
                        <div className="absolute inset-0 bg-otter-green/20 flex items-center justify-center">
                          <div className="bg-otter-green rounded-full p-1">
                            <Check className="h-5 w-5 text-white" />
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Preview button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreviewImage({ url: imageUrl, name: file.original_filename });
                      }}
                      className="absolute top-2 left-2 bg-otter-green text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      title="View full size"
                    >
                      <ImageIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(file.id);
                      }}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="h-4 w-4" />
                    </button>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 truncate">
                      {file.original_filename}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center pt-4 border-t">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {selectedFiles.size} file(s) selected
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSelectFiles}
              disabled={selectedFiles.size === 0}
              className="bg-otter-green hover:bg-otter-green-600 text-white"
            >
              Select {selectedFiles.size > 0 && `(${selectedFiles.size})`}
            </Button>
          </div>
        </div>
      </DialogContent>

      {/* Image Preview Modal */}
      {previewImage && (
        <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
          <DialogContent className="max-w-6xl max-h-[90vh] p-0">
            <div className="relative w-full h-full">
              <button
                onClick={() => setPreviewImage(null)}
                className="absolute top-4 right-4 z-10 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="p-6 flex flex-col items-center justify-center">
                <img
                  src={previewImage.url}
                  alt={previewImage.name}
                  className="max-w-full max-h-[80vh] object-contain"
                />
                <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                  {previewImage.name}
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
};
